import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import { ModalMessageSimple } from '@/common/components/ModalMessageSimple'
import { ShowHeader } from '@/common/components/ShowHeader'
import { useGoBack } from '@/common/hooks/useGoBack'
import useUserPermissions from '@/common/hooks/useUserPermissions'
import { FormTransformers } from '@/common/utils/formTransformers'
import TmaisFaqForm from '@/features/tmaisFaqs/components/TmaisFaqForm'
import useCreateTmaisFaq from '@/features/tmaisFaqs/hooks/useCreateTmaisFaq'
import { tmaisFaqFormSchema, TmaisFaqFormSchema } from '@/features/tmaisFaqs/schemas/form.schema'
import { zodResolver } from '@hookform/resolvers/zod'
import { useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { SubmitHandler, useForm } from 'react-hook-form'

const CreateTmaisFaq = () => {
  const goBack = useGoBack()
  const queryClient = useQueryClient()
  const [messages, setMessages] = useState<string[] | boolean>(false)

  const { hasUserPermission, isUserPermissionsPending } = useUserPermissions()

  const userHasPermissionToCreateFaq = isUserPermissionsPending || hasUserPermission('Admin::Tmais::Faqs::Create')

  const form = useForm<TmaisFaqFormSchema>({
    resolver: zodResolver(tmaisFaqFormSchema),
    defaultValues: {
      question: '',
      answer: '',
      position: '0',
    },
  })

  const { handleSubmit, setError } = form

  const { mutate: createFaq, isPending: createFaqIsPending } = useCreateTmaisFaq({
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tmaisFaqs'] })
      goBack({ fallback: '/tmais-faqs' })
    },
    onError: FormTransformers.errorsTransformer({ setError, setMessages }),
  })

  const onSubmit: SubmitHandler<TmaisFaqFormSchema> = (values) => createFaq(values)

  return (
    <ErrorMessageWrapper className="h-[calc(100vh-64px)]" statusCode={userHasPermissionToCreateFaq ? 200 : 403}>
      <form onSubmit={handleSubmit(onSubmit)} autoComplete="off" className="inner-screen">
        <ShowHeader
          goBack
          goBackProps={{ fallback: `/tmais-faqs` }}
          title="FAQs"
          subtitle="Novo FAQ"
          buttons={
            <div className="flex items-center gap-3">
              <Button type="button" variant="outline-error" onClick={() => goBack({ fallback: '/tmais-faqs' })}>
                Cancelar
              </Button>

              <Button
                type="submit"
                isLoading={createFaqIsPending}
                disabled={createFaqIsPending}
                operationKlassToCheckPermission="Admin::Tmais::Faqs::Create"
                data-testid="create_tmais_faq"
              >
                Confirmar
              </Button>
            </div>
          }
        />

        <Box className="flex flex-col gap-4 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] overflow-auto">
          <p className="text-lg font-bold">Novo FAQ</p>

          <TmaisFaqForm form={form} isSubmitting={createFaqIsPending} showPosition={false} />
        </Box>

        {messages && <ModalMessageSimple messages={messages} setMessages={setMessages} isError />}
      </form>
    </ErrorMessageWrapper>
  )
}

export default CreateTmaisFaq
